import { lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'react-proptypes';
import {useLocation, useNavigate} from 'react-router-dom';
import classNames from 'classnames';
import { ButtonComponent } from '@dnhb/node_package';
import { dispatchMapper } from '../../../shared/wizard/actions';
import { shapeInput, shapeOutput, shapeSettings } from '../config';
import { startAdvanced } from '../helpers';
import CloseSvg from 'images/shared/close.svg';
import InfoSvg from 'images/shared/info_icon.svg';
import NegativeSvg from 'images/hns/result_negative.svg';
import PositiveSvg from 'images/hns/result_positive.svg';

const getTitle = (key, props) => {
  const { cardType, input } = props;
  const uBent = input.applicant_has_partner ? 'U en/of uw partner zijn' : 'U bent';
  switch (`${cardType}:${key}`) {
    case 'applicant_age:<57':
      return <h3>{uBent} op het einde van de looptijd <strong>jonger dan 57</strong></h3>;
    case 'applicant_age:57-67':
      return <h3>{uBent} op het einde van de looptijd <strong>tussen de 57 & 67</strong></h3>;
    case 'applicant_age:>67':
      return <h3>{uBent} op het einde van de looptijd <strong>ouder dan 67</strong></h3>;
    case 'loan_to_value:<50':
      return <h3>Uw hypotheekschuld is <strong>minder dan 50%</strong> van de waarde van uw woning</h3>;
    case 'loan_to_value:50-80':
      return <h3>Uw hypotheekschuld is <strong>tussen de 50% en 80%</strong> van de waarde van uw woning</h3>;
    case 'loan_to_value:80-100':
      return <h3>Uw hypotheekschuld is <strong>tussen de 80% en 100%</strong> van de waarde van uw woning</h3>;
    case 'loan_to_value:>100':
      return <h3>Uw hypotheekschuld is <strong>meer dan 100%</strong> van de waarde van uw woning</h3>;
    case 'percentage_of_max_mortgage:<80':
      return (
        <h3>Het <strong>(pensioen)inkomen</strong> én/of de <strong>eigen middelen</strong> zijn onvoldoende</h3>
      );
    case 'percentage_of_max_mortgage:80-90':
      return <h3>Het <strong>(pensioen)inkomen</strong> en de <strong>eigen middelen</strong> zijn onvoldoende</h3>;
    case 'percentage_of_max_mortgage:90-100':
      return <h3>Het <strong>(pensioen)inkomen</strong> en de <strong>eigen middelen</strong> zijn onvoldoende</h3>;
    case 'percentage_of_max_mortgage:>100':
      return (
        <h3>Het <strong>(pensioen)inkomen</strong> en de <strong>eigen middelen</strong> zijn voldoende</h3>
      );
    case 'mortgage_duration:<5':
      return <h3>Einde van de looptijd is <strong>binnen 5 jaar</strong></h3>;
    case 'mortgage_duration:5-10':
      return <h3>Einde van de looptijd is <strong>tussen de 5 en 10 jaar</strong></h3>;
    case 'mortgage_duration:10-15':
      return <h3>Einde van de looptijd is <strong>tussen de 10 en 15 jaar</strong></h3>;
    case 'mortgage_duration:>15':
      return <h3>Einde van de looptijd is <strong>na meer dan 15 jaar</strong></h3>;
    default: return null;
  }
}

const getContent = (key, props) => {
  const { cardType, input } = props;
  switch (`${cardType}:${key}`) {
    case 'applicant_age:<57':
      return input.applicant_has_partner ? `
Na de einddatum van uw hypotheek gaan u en uw partner voorlopig nog niet met pensioen.
Vermoedelijk hebben jullie dan nog een geruime tijd recht op hypotheekrenteaftrek.
Geldverstrekkers houden dan voor de beoordeling van een hypotheek nog geen rekening met uw pensioeninkomen.` : `
Na de einddatum van uw hypotheek gaat u voorlopig nog niet met pensioen.
Vermoedelijk heeft u dan nog een geruime tijd recht op hypotheekrenteaftrek.
Geldverstrekkers houden dan voor de beoordeling van een hypotheek nog geen rekening met uw pensioeninkomen.`;
    case 'applicant_age:57-67':
      return input.applicant_has_partner ? `
Relatief kort na het einde van uw hypotheek gaat u en/of uw partner met pensioen.
Daarom is het pensioeninkomen dat jullie gaan ontvangen op dat moment al bepalend voor het verkrijgen of verlengen van
een hypotheek. Geldverstrekkers kijken dan voor de beoordeling van een hypotheek naar het pensioeninkomen dat u en/of
uw partner vanaf uw 67e ontvangen. ` : `
U gaat relatief kort na de einddatum van uw hypotheek met pensioen.
Daarom is het pensioeninkomen dat u gaat ontvangen al bepalend voor het verkrijgen of verlengen van een hypotheek.
Geldverstrekkers kijken dan voor de beoordeling van een hypotheek naar het pensioeninkomen dat u vanaf uw 67e gaat
ontvangen.`;
    case 'applicant_age:>67':
      return input.applicant_has_partner ? `
Na het einde van uw hypotheek hebben u en/of uw partner de pensioengerechtigde leeftijd bereikt.
Dat betekent een lagere belastingdruk, maar waarschijnlijk ook een lager inkomen dan tijdens uw werkzame periode.
Mogelijk hebben jullie binnenkort ook geen recht meer hebben op hypotheekrenteaftrek.` : `
Aan het eind van uw hypotheek heeft u de pensioengerechtigde leeftijd bereikt. Dat betekent een lagere belastingdruk,
maar waarschijnlijk ook een lager inkomen dan tijdens uw werkzame periode.
Vermoedelijk heeft u binnenkort ook geen recht meer op hypotheekrenteaftrek.`;
    case 'loan_to_value:<50':
      return `De waarde van uw woning is veel hoger dan uw hypotheekschuld.`;
    case 'loan_to_value:50-80':
      return `De waarde van uw woning is hoger dan uw hypotheekschuld.`;
    case 'loan_to_value:80-100':
      return `De waarde van uw woning ligt relatief dicht bij het bedrag van uw hypotheekschuld.`;
    case 'loan_to_value:>100':
      return `
De waarde van uw woning is lager dan uw hypotheekschuld.`;
    case 'percentage_of_max_mortgage:<80':
      return `Uw inkomen
en uw eigen middelen zijn samen onvoldoende om uw hypotheek op de einddatum te verlengen of te herfinancieren.`;
    case 'percentage_of_max_mortgage:80-90':
      return `Uw inkomen
en uw eigen middelen zijn samen onvoldoende om uw hypotheek op de einddatum te verlengen of te herfinancieren.`;
    case 'percentage_of_max_mortgage:90-100':
      return `Uw inkomen
en uw eigen middelen zijn samen voldoende om uw hypotheek op de einddatum te verlengen of te herfinancieren.`;
    case 'percentage_of_max_mortgage:>100':
      return `Uw inkomen
en uw eigen middelen zijn samen voldoende om uw hypotheek op de einddatum te verlengen of te herfinancieren.`;
    case 'mortgage_duration:<5':
      return `U heeft nog relatief weinig tijd om uw hypotheek aan te passen.`;
    case 'mortgage_duration:5-10':
      return `U heeft nog relatief weinig tijd om uw hypotheek aan te passen.`;
    case 'mortgage_duration:10-15':
      return `U heeft nog voldoende tijd om uw hypotheek aan te passen.`;
    case 'mortgage_duration:>15':
      return `U heeft nog de tijd om uw hypotheek aan te passen.`;
    default: return null;
  }
}

const Card = (props) => {
  const { dispatch, settings, pages, cardType, input, output, sync } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const config = output[cardType];
  const advanced = !settings.enablePremiumCalculation || location.state?.page !== 'results';
  if (!config) {
    return advanced ? null : (
      <div
        className="card advanced-calculation"
        role="presentation"
      >
        <div className="inner">
          <h3>
            <strong>Wilt u weten welk cijfer uw hypotheek scoort?</strong>
          </h3>
          <h3>
            Vul dan de hoogte van uw inkomen en eigen middelen in
          </h3>
          <div className="row input">
            <ButtonComponent
              value="Vul uw gegevens aan"
              name="next"
              id="next"
              label=""
              onClickHandler={() => startAdvanced(pages, dispatch, location, navigate)}
              showLabel={false}
              className="group col-xs-12"
              inputClassNames="rounded secondary"
            />
          </div>
        </div>
      </div>
    );
  }
  const activeCard = config.image === input.active_card ? null : config.image;
  const title = getTitle(config.key, props);
  const content = getContent(config.key, props);
  const ResultSvg = lazy(() => import(`images/hns/${config.image}`));
  return (
    <div
      role="presentation"
      className={classNames('card', { active: input.active_card === config.image })}
      onClick={() => sync('active_card', activeCard)}
    >
      <div className="inner">
        <div className="front">
          <Suspense fallback={<span />}><ResultSvg className="card-image" /></Suspense>
          {title}
          <small className="result-state">
            Meer informatie
            <InfoSvg className="state-image" />
          </small>
        </div>
        <div className="back">
          <CloseSvg className="corner-image" />
          {title}
          <p>{content}</p>
          {advanced && (
            <small className={classNames('result-state', config.positive ? 'positive' : 'negative')}>
              <div className="state-image">
                {config.positive ? <PositiveSvg /> : <NegativeSvg />}
              </div>
              Dit be&#xEF;nvloedt uw rapportcijfer {config.positive ? 'positief' : 'negatief'}
            </small>
          )}
        </div>
      </div>
    </div>
  );
}

Card.propTypes = {
  settings: shapeSettings.isRequired,
  dispatch: PropTypes.func.isRequired,
  sync: PropTypes.func.isRequired,
  input: shapeInput.isRequired,
  output: shapeOutput.isRequired,
  pages: PropTypes.arrayOf(PropTypes.string).isRequired,
  cardType: PropTypes.string.isRequired,
};

Card.defaultProps = {};

export default connect(state => ({
  settings: state.settings,
  syncing: state.syncing,
  output: state.output,
  input: state.input,
  pages: state.pages,
}), dispatchMapper)(Card)
